import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { tasksAPI, projectsAPI, usersAPI, uploadAPI, getFileUrl } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Card, PageHeader, Badge, Button, Modal, Table, Empty, Alert, FormField, FilterPills, SearchInput, Loading } from '../components/ui';
import { Plus, Pencil, Trash2, CheckCircle2, ExternalLink, Play, Square, Clock } from 'lucide-react';
import './Tasks.css';

const formatDuration = (totalSeconds) => {
  if (isNaN(totalSeconds) || totalSeconds < 0) totalSeconds = 0;
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

const getTaskHours = (task) => {
  if (!task || !task.timeLogs || !task.timeLogs.length) return '0.00';
  const total = task.timeLogs.reduce((acc, curr) => acc + parseFloat(curr.hours_spent || 0), 0);
  return total.toFixed(2);
};

const empty = { task_name: '', description: '', status: 'To Do', priority: 'Medium', project_id: '', assigned_to: '', verifier_id: '', attachment_url: '' };

export default function Tasks() {
  const { user: me, canCreate, canUpdate, canDelete } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const pendingTaskId = useRef(null);
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('');
  const [search, setSearch] = useState('');
  const [completionTarget, setCompletionTarget] = useState(null);
  const [completionForm, setCompletionForm] = useState({ completion_url: '', completion_note: '' });
  const [completionSaving, setCompletionSaving] = useState(false);
  const [verifyTarget, setVerifyTarget] = useState(null);
  const [verifyForm, setVerifyForm] = useState({ action: 'approve', review_comment: '' });
  const [verifySaving, setVerifySaving] = useState(false);
  const [detailsTarget, setDetailsTarget] = useState(null);
  const [activeTimer, setActiveTimer] = useState(null);
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [timerLoading, setTimerLoading] = useState(false);
  const [timerFeedback, setTimerFeedback] = useState(null);

  const getTimerStorageKey = (uid) => uid ? `active_task_timer_${uid}` : null;

  // Sync active timer strictly for the current authenticated user
  useEffect(() => {
    if (!me?.user_id) {
      setActiveTimer(null);
      setTimerSeconds(0);
      return;
    }

    const timerKey = getTimerStorageKey(me.user_id);

    tasksAPI.getActiveTimer()
      .then(res => {
        if (res.data?.data) {
          const s = res.data.data;
          const active = {
            task_id: s.task_id,
            task_name: s.task?.task_name || 'Active Task',
            start_time: s.start_time,
            session_id: s.session_id,
            user_id: me.user_id,
          };
          setActiveTimer(active);
          try {
            if (timerKey) localStorage.setItem(timerKey, JSON.stringify(active));
          } catch(e) {}
        } else {
          // Current user has NO active timer on the server — clear any stale data!
          setActiveTimer(null);
          setTimerSeconds(0);
          try {
            if (timerKey) localStorage.removeItem(timerKey);
            localStorage.removeItem('active_task_timer'); // Clear old un-scoped key
          } catch(e) {}
        }
      })
      .catch(() => {
        // Fallback on network failure only for this user's scoped key
        try {
          if (timerKey) {
            const cached = localStorage.getItem(timerKey);
            if (cached) {
              const parsed = JSON.parse(cached);
              if (Number(parsed.user_id) === Number(me.user_id)) {
                setActiveTimer(parsed);
                return;
              }
            }
          }
          setActiveTimer(null);
        } catch(e) {
          setActiveTimer(null);
        }
      });
  }, [me?.user_id]);

  // Tick timer every second
  useEffect(() => {
    if (!activeTimer?.start_time) {
      setTimerSeconds(0);
      return;
    }
    const updateElapsed = () => {
      const startMs = new Date(activeTimer.start_time).getTime();
      const now = Date.now();
      const diff = Math.max(0, Math.floor((now - startMs) / 1000));
      setTimerSeconds(diff);
    };
    updateElapsed();
    const interval = setInterval(updateElapsed, 1000);
    return () => clearInterval(interval);
  }, [activeTimer]);

  const handleStartTimer = async (task, e) => {
    if (e) e.stopPropagation();
    setTimerLoading(true);
    setTimerFeedback(null);
    try {
      const res = await tasksAPI.startTimer(task.task_id);
      const session = res.data.activeSession;
      const timerData = {
        task_id: task.task_id,
        task_name: task.task_name,
        start_time: session.start_time || new Date().toISOString(),
        session_id: session.session_id,
        user_id: me?.user_id,
      };
      setActiveTimer(timerData);
      const timerKey = getTimerStorageKey(me?.user_id);
      try {
        if (timerKey) localStorage.setItem(timerKey, JSON.stringify(timerData));
        localStorage.removeItem('active_task_timer'); // Clear legacy un-scoped key
      } catch(e) {}
      setTimerFeedback({ type: 'success', message: `Timer started for "${task.task_name}". Time is now calculating!` });
      load();
    } catch (err) {
      setTimerFeedback({ type: 'error', message: err.response?.data?.message || 'Error starting timer' });
    } finally {
      setTimerLoading(false);
    }
  };

  const handleStopTimer = async (taskId, e) => {
    if (e) e.stopPropagation();
    setTimerLoading(true);
    setTimerFeedback(null);
    try {
      const res = await tasksAPI.stopTimer(taskId);
      setActiveTimer(null);
      setTimerSeconds(0);
      const timerKey = getTimerStorageKey(me?.user_id);
      try {
        if (timerKey) localStorage.removeItem(timerKey);
        localStorage.removeItem('active_task_timer');
      } catch(e) {}
      setTimerFeedback({
        type: 'success',
        message: `⏱️ Timer stopped: Calculated and logged ${res.data.hours_spent} hrs (${res.data.duration_minutes || 1} min) on task.`
      });
      load();
    } catch (err) {
      setTimerFeedback({ type: 'error', message: err.response?.data?.message || 'Error stopping timer' });
    } finally {
      setTimerLoading(false);
    }
  };

  const load = () => {
    setLoading(true);
    const requests = [tasksAPI.list(), projectsAPI.list()];
    if (canCreate) requests.push(usersAPI.list());
    Promise.all(requests)
      .then(([t, p, u]) => {
        setTasks(t.data.data || []);
        setProjects(p.data.data || []);
        if (canCreate) setUsers(u?.data?.data || []);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    const id = searchParams.get('task_id');
    if (id) pendingTaskId.current = id;
  }, [searchParams]);

  const openCreate = () => { setEditing(null); setForm(empty); setError(''); setModal(true); };
  const openEdit = (t) => { setEditing(t); setForm({ task_name: t.task_name, description: t.description||'', status: t.status, priority: t.priority, project_id: t.project_id, assigned_to: t.assigned_to||'', verifier_id: t.verifier_id||'', attachment_url: t.attachment_url||'' }); setError(''); setModal(true); };

  useEffect(() => {
    if (loading || !tasks.length || !pendingTaskId.current) return;
    const task = tasks.find(t => String(t.task_id) === String(pendingTaskId.current));
    if (task) {
      openEdit(task);
      pendingTaskId.current = null;
      const next = new URLSearchParams(searchParams);
      next.delete('task_id');
      setSearchParams(next, { replace: true });
    }
  }, [loading, tasks]);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    setSaving(true);
    try {
      const res = await uploadAPI.single(formData);
      setForm(f => ({ ...f, attachment_url: res.data.data.url }));
    } catch (err) {
      setError(err.response?.data?.message || 'File upload failed');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      if (editing) await tasksAPI.update(editing.task_id, form);
      else await tasksAPI.create(form);
      setModal(false); load();
    } catch (err) {
      setError(err.response?.data?.message || 'Error saving task');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this task?')) return;
    await tasksAPI.delete(id);
    load();
  };

  const openCompletion = (task) => {
    setCompletionTarget(task);
    setCompletionForm({ completion_url: '', completion_note: '' });
    setError('');
  };

  const handleComplete = async (e) => {
    e.preventDefault();
    setCompletionSaving(true);
    setError('');
    try {
      await tasksAPI.complete(completionTarget.task_id, completionForm);
      setCompletionTarget(null);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to submit task for review');
    } finally {
      setCompletionSaving(false);
    }
  };

  const openVerify = (task) => {
    setVerifyTarget(task);
    setVerifyForm({ action: 'approve', review_comment: '' });
    setError('');
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setVerifySaving(true);
    setError('');
    try {
      await tasksAPI.verify(verifyTarget.task_id, verifyForm);
      setVerifyTarget(null);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to verify task');
    } finally {
      setVerifySaving(false);
    }
  };

  const filtered = tasks.filter(t => {
    let match = true;
    const lower = search.toLowerCase();
    if (filter) match = t.status === filter;
    if (search) {
      match = match && (
        t.task_name?.toLowerCase().includes(lower) ||
        t.project?.project_name?.toLowerCase().includes(lower) ||
        t.project?.project_code?.toLowerCase().includes(lower)
      );
    }
    return match;
  });

  // UI stats and chart (styled similar to Projects page)
  const STATUS_OPTIONS = ['To Do', 'In Progress', 'In Review', 'Pending Review', 'Rework', 'Done'];
  const total = tasks.length;
  const statusColors = {
    'To Do': 'var(--text-muted)',
    'In Progress': 'var(--accent)',
    'In Review': 'var(--accent-light)',
    'Pending Review': '#f59e0b',
    'Rework': '#ef4444',
    'Done': 'var(--accent3)'
  };
  const statusGroups = STATUS_OPTIONS.map(status => ({
    status,
    count: tasks.filter(t => t.status === status).length,
    color: statusColors[status] || 'var(--text-muted)'
  }));
  const activeGroups = statusGroups.filter(g => g.count > 0);
  // build simple pie path slices
  const PIE_R = 40, PIE_CX = 50, PIE_CY = 50;
  let cumAngle = -90;
  const pieSlices = activeGroups.map(g => {
    const pct = total > 0 ? g.count / total : 0;
    const startAngle = cumAngle;
    cumAngle += pct * 360;
    const endAngle = cumAngle;
    const large = pct > 0.5 ? 1 : 0;
    const toRad = deg => (deg * Math.PI) / 180;
    const sx = PIE_CX + PIE_R * Math.cos(toRad(startAngle));
    const sy = PIE_CY + PIE_R * Math.sin(toRad(startAngle));
    const ex = PIE_CX + PIE_R * Math.cos(toRad(endAngle));
    const ey = PIE_CY + PIE_R * Math.sin(toRad(endAngle));
    return { ...g, pct, d: `M ${PIE_CX} ${PIE_CY} L ${sx} ${sy} A ${PIE_R} ${PIE_R} 0 ${large} 1 ${ex} ${ey} Z` };
  });

  const columns = [
    { key: 'task_name', label: 'Task', render: r => (
      <span 
        onClick={() => setDetailsTarget(r)} 
        style={{ fontWeight: 600, cursor: 'pointer', color: 'var(--accent)', textDecoration: 'underline' }}
      >
        {r.task_name}
      </span>
    ) },
    { key: 'project', label: 'Project', render: r => {
      const code = r.project?.project_code;
      const name = r.project?.project_name || '—';
      return code ? `${code} • ${name}` : name;
    } },
    { key: 'assignee', label: 'Assigned To', render: r => r.assignee?.name || '—' },
    { key: 'priority', label: 'Priority', render: r => <Badge value={r.priority} /> },
    { key: 'status', label: 'Status', render: r => <Badge value={r.status} /> },
    { key: 'hours', label: 'Hours', render: r => {
      const isCurrentActive = activeTimer && Number(activeTimer.task_id) === Number(r.task_id);
      const totalHours = getTaskHours(r);
      return (
        <div className="task-hours-display">
          <span className="task-hours-value" style={{ color: Number(totalHours) > 0 ? 'var(--accent, #f59e0b)' : 'var(--text-muted)' }}>
            {totalHours}h
          </span>
          {isCurrentActive && (
            <span className="task-hours-active-badge">
              <span className="timer-pulse-dot" style={{ width: 6, height: 6 }} />
              {formatDuration(timerSeconds)}
            </span>
          )}
        </div>
      );
    } },
    { key: 'timer_control', label: 'Timer', render: r => {
      const isCurrentActive = activeTimer && Number(activeTimer.task_id) === Number(r.task_id);
      if (isCurrentActive) {
        return (
          <button
            type="button"
            className="task-timer-btn task-timer-btn--stop"
            onClick={(e) => handleStopTimer(r.task_id, e)}
            disabled={timerLoading}
            title="Click to stop timer and calculate logged hours"
          >
            <Square size={12} fill="#ffffff" />
            <span>Stop ({formatDuration(timerSeconds)})</span>
          </button>
        );
      }
      if (['Done', 'Pending Review'].includes(r.status)) {
        return <span style={{ color: 'var(--text-muted)', fontSize: 13, padding: '0 8px' }}>—</span>;
      }
      return (
        <button
          type="button"
          className="task-timer-btn task-timer-btn--start"
          onClick={(e) => handleStartTimer(r, e)}
          disabled={timerLoading}
          title="Start timer for this task"
        >
          <Play size={12} fill="#22c55e" />
          <span>Start</span>
        </button>
      );
    } },
    { key: 'proof', label: 'Git proof', render: r => r.completion_url ? (
      <a href={getFileUrl(r.completion_url)} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} style={{ color: 'var(--accent)', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
        View <ExternalLink size={12} />
      </a>
    ) : '—' },
    { key: 'attachment', label: 'Attachment', render: r => r.attachment_url ? (
      <a href={getFileUrl(r.attachment_url)} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} style={{ color: 'var(--accent)', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
        File <ExternalLink size={12} />
      </a>
    ) : '—' },
    { key: 'actions', label: '', render: r => {
      const isAssignee = Number(r.assigned_to) === Number(me?.user_id);
      const isVerifier = Number(r.verifier_id) === Number(me?.user_id);
      const canVerify = me?.role === 'Admin'; // Only Admins can verify
      return (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {/* Submit for review: assignee on tasks not yet done/pending */}
          {isAssignee && !['Done', 'Pending Review'].includes(r.status) && (
            <Button size="sm" onClick={e => { e.stopPropagation(); openCompletion(r); }}><CheckCircle2 size={13} /> Submit</Button>
          )}
          {/* Verify actions: show for verifier on Pending Review tasks */}
          {canVerify && r.status === 'Pending Review' && (
            <Button size="sm" variant="ghost" style={{ background: 'rgba(34,197,94,0.12)', color: '#22c55e', border: '1px solid rgba(34,197,94,0.3)' }} onClick={e => { e.stopPropagation(); openVerify(r); }}>🔍 Review</Button>
          )}
          {canUpdate && <Button size="sm" variant="ghost" onClick={e => { e.stopPropagation(); openEdit(r); }}><Pencil size={13} /></Button>}
          {canDelete && <Button size="sm" variant="danger" onClick={e => { e.stopPropagation(); handleDelete(r.task_id); }}><Trash2 size={13} /></Button>}
        </div>
      );
    }}
  ];

  return (
    <div className="animate-in">
      <PageHeader 
        title="Tasks" 
        subtitle={`${filtered.length} task${filtered.length !== 1 ? 's' : ''}`} 
        eyebrow="Work management" 
      />

      {timerFeedback && (
        <div style={{ marginBottom: 16 }}>
          <Alert type={timerFeedback.type} message={timerFeedback.message} />
        </div>
      )}

      {activeTimer && (
        <div className="active-task-timer-banner">
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <span className="timer-pulse-dot" />
            <div>
              <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#22c55e', fontWeight: 800 }}>
                Active Task Timer • Tracking Time
              </div>
              <div style={{ fontWeight: 700, fontSize: 15, color: '#ffffff', marginTop: 2 }}>
                {activeTimer.task_name}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ fontFamily: 'monospace', fontSize: 22, fontWeight: 800, color: '#22c55e', letterSpacing: '1px', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Clock size={18} /> {formatDuration(timerSeconds)}
            </div>
            <button
              type="button"
              className="task-timer-btn task-timer-btn--stop"
              style={{ padding: '8px 16px', fontSize: 13 }}
              onClick={(e) => handleStopTimer(activeTimer.task_id, e)}
              disabled={timerLoading}
            >
              <Square size={14} fill="#ffffff" />
              <span>Stop & Calculate Hours</span>
            </button>
          </div>
        </div>
      )}

      <Card className="projects-page__toolbar" glass>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 24, padding: '16px 24px' }}>
          <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
            <div style={{ position: 'relative', flexShrink: 0 }}>
              <svg width={100} height={100} aria-hidden="true">
                {pieSlices.map(s => (
                  <path key={s.status} d={s.d} fill={s.color}
                    opacity={filter === s.status ? 1 : filter ? 0.35 : 0.9}
                    stroke="var(--bg-card)" strokeWidth={2}
                    style={{ cursor: 'pointer', transition: 'opacity 0.2s' }}
                    onClick={() => setFilter(prev => prev === s.status ? '' : s.status)} />
                ))}
                <circle cx={PIE_CX} cy={PIE_CY} r={20} fill="var(--bg-card)" />
              </svg>
              <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                <span style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 800, color: 'var(--text)', lineHeight: 1 }}>{filtered.length}</span>
                <span style={{ fontSize: 9, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: 2 }}>{filter || 'All'}</span>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button type="button" onClick={() => setFilter('')}
                style={{
                  padding: '8px 14px', borderRadius: 999, border: filter === '' ? '1px solid transparent' : '1px solid var(--border)',
                  background: filter === '' ? 'linear-gradient(135deg, var(--accent), var(--accent-light))' : 'var(--bg-card)',
                  color: filter === '' ? '#fff' : 'var(--text-muted)', cursor: 'pointer', fontSize: 12.5, fontWeight: 600,
                  fontFamily: 'var(--font-body)', transition: 'all 0.15s', display: 'inline-flex', alignItems: 'center', gap: 7,
                  boxShadow: filter === '' ? '0 4px 14px var(--accent-glow)' : 'none'
                }}>
                All
                <span style={{ fontSize: 11, fontWeight: 700, padding: '1px 7px', borderRadius: 999, background: filter === '' ? 'rgba(255,255,255,0.18)' : 'var(--bg-hover)' }}>{total}</span>
              </button>
              {statusGroups.map(g => (
                <button key={g.status} type="button" onClick={() => setFilter(prev => prev === g.status ? '' : g.status)}
                  style={{
                    padding: '8px 14px', borderRadius: 999, border: filter === g.status ? '1px solid transparent' : '1px solid var(--border)',
                    background: filter === g.status ? g.color : 'var(--bg-card)',
                    color: filter === g.status ? '#fff' : 'var(--text-muted)', cursor: 'pointer', fontSize: 12.5, fontWeight: 600,
                    fontFamily: 'var(--font-body)', transition: 'all 0.15s', display: 'inline-flex', alignItems: 'center', gap: 7,
                    boxShadow: filter === g.status ? `0 4px 14px color-mix(in srgb, ${g.color} 40%, transparent)` : 'none'
                  }}>
                  {g.status}
                  <span style={{ fontSize: 11, fontWeight: 700, padding: '1px 7px', borderRadius: 999, background: filter === g.status ? 'rgba(255,255,255,0.18)' : 'var(--bg-hover)', color: filter === g.status ? '#fff' : 'var(--text-dim)' }}>{g.count}</span>
                </button>
              ))}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <SearchInput value={search} onChange={setSearch} placeholder="Search tasks..." style={{ minWidth: 240 }} />
            {canCreate && <Button onClick={openCreate} variant="primary"><Plus size={15} /> New Task</Button>}
          </div>
        </div>
      </Card>

      <Card>
        {loading ? <Loading />
          : filtered.length === 0 ? <Empty message="No tasks found" hint="Create a task or adjust filters" />
          : <Table columns={columns} data={filtered} />}
      </Card>

      <Modal open={modal} onClose={() => setModal(false)} title={editing ? 'Edit Task' : 'New Task'}>
        <Alert type="error" message={error} />
        <form onSubmit={handleSave}>
          <FormField label="Task Name">
            <input value={form.task_name} onChange={e => setForm(f => ({...f, task_name: e.target.value}))} required placeholder="Task name" />
          </FormField>
          <FormField label="Description">
            <textarea rows={3} value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} placeholder="Optional description..." style={{ resize: 'vertical' }} />
          </FormField>
          <FormField label="Project">
            <select value={form.project_id} onChange={e => setForm(f => ({...f, project_id: e.target.value}))} required>
              <option value="">Select project...</option>
              {projects.map(p => (
                <option key={p.project_id} value={p.project_id}>
                  {p.project_code ? `${p.project_code} • ${p.project_name}` : p.project_name}
                </option>
              ))}
            </select>
          </FormField>
          <FormField label="Assign To">
            <select value={form.assigned_to} onChange={e => setForm(f => ({...f, assigned_to: e.target.value}))}>
              <option value="">Unassigned</option>
              {(() => {
                const selectedProject = projects.find(p => String(p.project_id) === String(form.project_id));
                const allowedUserIds = selectedProject ? (() => {
                  const ids = new Set();
                  if (selectedProject.project_lead_id) ids.add(Number(selectedProject.project_lead_id));
                  if (selectedProject.manager_id) ids.add(Number(selectedProject.manager_id));
                  if (selectedProject.client_delivery_id) ids.add(Number(selectedProject.client_delivery_id));
                  if (selectedProject.members) {
                    let membersArr = [];
                    try {
                      membersArr = typeof selectedProject.members === 'string' ? JSON.parse(selectedProject.members) : selectedProject.members;
                    } catch (e) {}
                    if (Array.isArray(membersArr)) {
                      membersArr.forEach(id => ids.add(Number(id)));
                    }
                  }
                  return ids;
                })() : null;
                const filteredUsers = allowedUserIds ? users.filter(u => allowedUserIds.has(Number(u.user_id))) : users;
                return filteredUsers.map(u => <option key={u.user_id} value={u.user_id}>{u.name}</option>);
              })()}
            </select>
          </FormField>
          <FormField label="Verifier (reviews completion)">
            <select value={form.verifier_id} onChange={e => setForm(f => ({...f, verifier_id: e.target.value}))}>
              <option value="">None (Project Lead will verify)</option>
              {users.map(u => <option key={u.user_id} value={u.user_id}>{u.name} ({u.role})</option>)}
            </select>
          </FormField>
          <FormField label="Attach File">
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <input type="file" onChange={handleFileUpload} />
              {form.attachment_url && (
                <a href={getFileUrl(form.attachment_url)} target="_blank" rel="noreferrer" style={{ fontSize: 13, color: 'var(--accent)' }}>View</a>
              )}
            </div>
          </FormField>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <FormField label="Status">
              <select value={form.status} onChange={e => setForm(f => ({...f, status: e.target.value}))}>
                {['To Do','In Progress','In Review','Done'].map(s => <option key={s}>{s}</option>)}
              </select>
            </FormField>
            <FormField label="Priority">
              <select value={form.priority} onChange={e => setForm(f => ({...f, priority: e.target.value}))}>
                {['Low','Medium','High','Critical'].map(s => <option key={s}>{s}</option>)}
              </select>
            </FormField>
          </div>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 8 }}>
            <Button variant="secondary" onClick={() => setModal(false)} type="button">Cancel</Button>
            <Button type="submit" disabled={saving}>{saving ? 'Saving...' : editing ? 'Update' : 'Create'}</Button>
          </div>
        </form>
      </Modal>

      <Modal open={Boolean(completionTarget)} onClose={() => setCompletionTarget(null)} title="Submit Task for Review">
        <Alert type="error" message={error} />
        <div style={{ marginBottom: 12, padding: '10px 14px', background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 8, fontSize: 13, color: '#f59e0b' }}>
          ⏳ This task will be sent to your verifier for approval before being marked as Done.
        </div>
        <form onSubmit={handleComplete}>
          <FormField label="GitHub commit or pull request URL">
            <input type="url" value={completionForm.completion_url} onChange={e => setCompletionForm(f => ({ ...f, completion_url: e.target.value }))} required placeholder="https://github.com/owner/repository/pull/123" />
          </FormField>
          <FormField label="Completion note">
            <textarea rows={3} value={completionForm.completion_note} onChange={e => setCompletionForm(f => ({ ...f, completion_note: e.target.value }))} placeholder="Briefly describe what was completed" />
          </FormField>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
            <Button variant="secondary" onClick={() => setCompletionTarget(null)}>Cancel</Button>
            <Button type="submit" disabled={completionSaving}>{completionSaving ? 'Submitting...' : 'Submit for Review'}</Button>
          </div>
        </form>
      </Modal>

      {/* Verify Modal — for verifiers to approve or reject */}
      <Modal open={Boolean(verifyTarget)} onClose={() => setVerifyTarget(null)} title="Review Task">
        <Alert type="error" message={error} />
        {verifyTarget && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 4 }}>Task</div>
            <div style={{ fontWeight: 600, fontSize: 15 }}>{verifyTarget.task_name}</div>
            {verifyTarget.completion_note && (
              <div style={{ marginTop: 12, padding: '10px 14px', background: 'var(--bg-hover)', borderRadius: 8, fontSize: 13 }}>
                <div style={{ fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>Member's note:</div>
                <div>{verifyTarget.completion_note}</div>
              </div>
            )}
            {verifyTarget.completion_url && (
              <div style={{ marginTop: 8 }}>
                <a href={getFileUrl(verifyTarget.completion_url)} target="_blank" rel="noreferrer" style={{ color: 'var(--accent)', fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 4 }}>View proof <ExternalLink size={12} /></a>
              </div>
            )}
          </div>
        )}
        <form onSubmit={handleVerify}>
          <FormField label="Decision">
            <div style={{ display: 'flex', gap: 12 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '10px 16px', borderRadius: 8, border: `2px solid ${verifyForm.action === 'approve' ? '#22c55e' : 'var(--border)'}`, background: verifyForm.action === 'approve' ? 'rgba(34,197,94,0.08)' : 'var(--bg-hover)', flex: 1, justifyContent: 'center', transition: 'all 0.15s' }}>
                <input type="radio" name="action" value="approve" checked={verifyForm.action === 'approve'} onChange={e => setVerifyForm(f => ({ ...f, action: e.target.value }))} style={{ display: 'none' }} />
                <span style={{ fontSize: 18 }}>✅</span> <span style={{ fontWeight: 600, color: verifyForm.action === 'approve' ? '#22c55e' : 'var(--text-muted)' }}>Approve</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '10px 16px', borderRadius: 8, border: `2px solid ${verifyForm.action === 'reject' ? '#ef4444' : 'var(--border)'}`, background: verifyForm.action === 'reject' ? 'rgba(239,68,68,0.08)' : 'var(--bg-hover)', flex: 1, justifyContent: 'center', transition: 'all 0.15s' }}>
                <input type="radio" name="action" value="reject" checked={verifyForm.action === 'reject'} onChange={e => setVerifyForm(f => ({ ...f, action: e.target.value }))} style={{ display: 'none' }} />
                <span style={{ fontSize: 18 }}>🔁</span> <span style={{ fontWeight: 600, color: verifyForm.action === 'reject' ? '#ef4444' : 'var(--text-muted)' }}>Reject (Rework)</span>
              </label>
            </div>
          </FormField>
          {verifyForm.action === 'reject' && (
            <FormField label="Review Comment (required for rejection)">
              <textarea rows={3} value={verifyForm.review_comment} onChange={e => setVerifyForm(f => ({ ...f, review_comment: e.target.value }))} placeholder="Explain what needs to be corrected..." required />
            </FormField>
          )}
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 8 }}>
            <Button variant="secondary" type="button" onClick={() => setVerifyTarget(null)}>Cancel</Button>
            <Button type="submit" disabled={verifySaving} style={{ background: verifyForm.action === 'approve' ? 'linear-gradient(135deg,#22c55e,#16a34a)' : 'linear-gradient(135deg,#ef4444,#b91c1c)' }}>
              {verifySaving ? 'Processing...' : verifyForm.action === 'approve' ? '✅ Approve Task' : '🔁 Send for Rework'}
            </Button>
          </div>
        </form>
      </Modal>

      <Modal open={Boolean(detailsTarget)} onClose={() => setDetailsTarget(null)} title="Task Details">
        {detailsTarget && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>Task Name</div>
              <div style={{ fontSize: 14 }}>{detailsTarget.task_name}</div>
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>Description</div>
              <div style={{ fontSize: 14, whiteSpace: 'pre-wrap' }}>{detailsTarget.description || 'No description provided.'}</div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>Project</div>
                <div style={{ fontSize: 14 }}>
                  {detailsTarget.project?.project_code ? `${detailsTarget.project.project_code} • ${detailsTarget.project.project_name}` : detailsTarget.project?.project_name || '—'}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>Assigned To</div>
                <div style={{ fontSize: 14 }}>{detailsTarget.assignee?.name || 'Unassigned'}</div>
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>Status</div>
                <div><Badge value={detailsTarget.status} /></div>
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>Priority</div>
                <div><Badge value={detailsTarget.priority} /></div>
              </div>
            </div>
            {detailsTarget.attachment_url && (
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>Attachment</div>
                <a href={getFileUrl(detailsTarget.attachment_url)} target="_blank" rel="noreferrer" style={{ fontSize: 14, color: 'var(--accent)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  View Attachment <ExternalLink size={14} />
                </a>
              </div>
            )}

            {/* Time Tracking Section */}
            <div style={{ padding: '14px', borderRadius: 10, background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', letterSpacing: '0.05em' }}>Time Tracking</div>
                  <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--accent, #f59e0b)', marginTop: 2 }}>
                    {getTaskHours(detailsTarget)} hrs logged
                  </div>
                </div>
                <div>
                  {activeTimer && Number(activeTimer.task_id) === Number(detailsTarget.task_id) ? (
                    <button
                      type="button"
                      className="task-timer-btn task-timer-btn--stop"
                      style={{ padding: '8px 16px', fontSize: 13 }}
                      onClick={(e) => handleStopTimer(detailsTarget.task_id, e)}
                      disabled={timerLoading}
                    >
                      <Square size={13} fill="#ffffff" />
                      <span>Stop Timer ({formatDuration(timerSeconds)})</span>
                    </button>
                  ) : ['Done', 'Pending Review'].includes(detailsTarget.status) ? (
                    <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', padding: '6px 12px', background: 'var(--bg-hover)', borderRadius: 6 }}>
                      {detailsTarget.status === 'Done' ? '✅ Completed' : '⏳ Under Review'}
                    </span>
                  ) : (
                    <button
                      type="button"
                      className="task-timer-btn task-timer-btn--start"
                      style={{ padding: '8px 16px', fontSize: 13 }}
                      onClick={(e) => handleStartTimer(detailsTarget, e)}
                      disabled={timerLoading}
                    >
                      <Play size={13} fill="#22c55e" />
                      <span>Start Timer</span>
                    </button>
                  )}
                </div>
              </div>
              {detailsTarget.timeLogs && detailsTarget.timeLogs.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 10, borderTop: '1px solid var(--border)', paddingTop: 10 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)' }}>Logged Sessions:</div>
                  {detailsTarget.timeLogs.slice(0, 5).map((log, i) => (
                    <div key={log.log_id || i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--text-secondary)' }}>
                      <span>{log.date || 'Today'} • {log.notes || 'Timer session'}</span>
                      <span style={{ fontWeight: 700, color: '#ffffff' }}>{log.hours_spent}h</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic', marginTop: 4 }}>
                  No time logged yet. Click Start to begin tracking!
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 16 }}>
              <Button variant="secondary" onClick={() => setDetailsTarget(null)}>Close</Button>
              {canUpdate && (
                <Button onClick={() => { setDetailsTarget(null); openEdit(detailsTarget); }}>
                  <Pencil size={14} style={{ marginRight: 6 }} /> Edit Task
                </Button>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
